import { object, string, number, array, boolean } from 'yup';
import { validatePhoneNumber } from '~/pages/LiveTracking/utils';
import moment from 'moment';
import { isEmpty } from 'lodash';

// Driver Verification

const DriverAddressSchema = object().shape({
  address_line_1: string().trim().required('Address Line 1 is required'),
  country: string().required('Country is required'),
  state: string().required('State is required'),
  city: string().trim().required('City is required'),
  postcode: string().trim().required('Postcode is required'),
  phone_number: string().trim().required('Phone number is required'),
  email: string().trim().email('Invalid email').required('Email is required'),
});

const DriverVerificationSchema = object().shape({});

const DriverAgeSchema = object().shape({
  first_name: string().trim().required('First Name is required'),
  last_name: string().trim().required('Last Name is required'),
  date_of_birth: string().required('Date of birth is required'),
});

const DriverSinNumberSchema = object().shape({
  sin_number: string().trim().required('SIN Number is required'),
});

const DriverVehiclesSchema = object().shape({
  vehicle: object().shape({
    // make_model: string().required('Make / Brand is a required field'),
    // color: string().required('Color is a required field'),
    // licence_plate: string().required('Plate No. is a required field'),
  }),
});

const DriverWeeklyHoursSchema = object().shape({
  preferred_areas: array().of(
    object().shape({
      name: string().required('Name is a required field'),
      address_line_1: string().required('Address Line 1 is a required field'),
      country: string().required('Country is a required field'),
      state: string().required('Province / State is a required field'),
      city: string().required('City is a required field'),
      postcode: string().required('Postal Code is a required field'),
      radius: number()
        .required('Radius is a required field')
        .moreThan(0, 'Must be more than 0')
        .max(25, 'Must be less than or equal to 25'),
    }),
  ),
});

const DriverPaymentBankingSchema = object().shape({
  payment_detail: object().shape({
    transit_number: string().required('Transit Number is a required field'),
    institution_number: string().required(
      'Institution Number is a required field',
    ),
    account_number: string().required('Account Number is a required field'),
    payment_frequency: string().required(
      'Payment Frequency is a required field',
    ),
  }),
});

// Driver Detail

const DriverProfileSchema = object().shape({
  first_name: string().trim().required('First Name is required'),
  last_name: string().trim().required('Last Name is required'),
  display_name: string()
    .required('This field is required')
    .trim()
    .max(50, 'Max 50 characters'),
  phone: string().trim().required('Phone number is required'),
  email: string().email('Invalid email address'),
  customer_support: string(),
  alternative_phone_country_code: string(),
  alternative_phone: string().when('customer_support', {
    is: (v) => v && v === 'contact_alternative_phone',
    then: string()
      .trim()
      .required('Alternative Phone number is required')
      .test(
        'alternative_phone',
        'Invalid phone number',
        (value: any, testContext: any) => {
          testContext.parent.country_code =
            testContext.parent.alternative_phone_country_code || 'CA';
          return validatePhoneNumber(value, testContext);
        },
      ),
  }),
});

const DriverSettingSchema = object().shape({
  commission_rate: string().trim().required('Commission Rate is required'),
});

const ShiftSchema = object().shape({
  start_time: string()
    .required('Please select a Start Time value')
    .test(
      'is-before-end',
      'Start time must be before End time',
      function (start_time) {
        const { end_time } = this.parent;
        if (
          (start_time === '12:00' && end_time === '12:00') ||
          moment(end_time, 'hh:mm').isSameOrBefore(moment(start_time, 'hh:mm'))
        ) {
          return false;
        }
        return true;
      },
    ),
  end_time: string().required('Please select an End Time value'),
  pause_assign: number(),
});

const AddSpecialShift = object().shape({
  name: string().trim().required('Please enter a Name'),
  start_date: string()
    .required('Please select a Start Date value')
    .test(
      'start_date',
      'Start date must be before end date.',
      // eslint-disable-next-line func-names
      function (value) {
        const { end_date } = this.parent;
        if (!value || !end_date) return true;
        return moment(this.parent.end_date).isSameOrAfter(moment(value));
      },
    ),
  end_date: string()
    .required('Please select an End Date value')
    // eslint-disable-next-line func-names
    .test('end_date', 'End date must be after start date.', function (value) {
      const { start_date } = this.parent;
      if (!value || !start_date) return true;
      return moment(this.parent.start_date).isSameOrBefore(moment(value));
    }),
  available: boolean().required(),
  all_hour: boolean().required(),
  shifts: array().when(['available', 'all_hour'], {
    is: (available, all_hour) => available === true && all_hour === false,
    then: array()
      .of(ShiftSchema)
      .min(1, 'Please add at least one Shift')
      .test('unique-time-slot', 'Shift must be unique', function (shifts) {
        if (!shifts) return true;

        // Map to track duplicates
        const seen = new Map();
        const errors = {};

        // Iterate through each time slot and check for duplicates
        shifts.forEach((shift, index) => {
          const key = `${shift.start_time}-${shift.end_time}`;
          if (seen.has(key)) {
            // Mark the current and original duplicate entries with an error
            errors[index] = {
              start_time: 'This shift is duplicated',
              end_time: ' ',
            };
          } else {
            seen.set(key, index);
          }
        });

        // If errors exist, return them in the expected format
        if (!isEmpty(errors)) {
          return this.createError({
            path: 'shifts',
            message: errors,
          });
        }
        return true;
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const AddShift = object().shape({
  available: boolean().required(),
  all_hour: boolean().required(),
  shifts: array().when(['available', 'all_hour'], {
    is: (available, all_hour) => available === true && all_hour === false,
    then: array()
      .of(ShiftSchema)
      .min(1, 'Please add at least one Shift')
      .test('unique-time-slot', 'Shift must be unique', function (shifts) {
        if (!shifts) return true;

        // Map to track duplicates
        const seen = new Map();
        const errors = {};

        // Iterate through each time slot and check for duplicates
        shifts.forEach((shift, index) => {
          const key = `${shift.start_time}-${shift.end_time}`;
          if (seen.has(key)) {
            // Mark the current and original duplicate entries with an error
            errors[index] = {
              start_time: 'This shift is duplicated',
              end_time: ' ',
            };
          } else {
            seen.set(key, index);
          }
        });

        // If errors exist, return them in the expected format
        if (!isEmpty(errors)) {
          return this.createError({
            path: 'shifts',
            message: errors,
          });
        }
        return true;
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export {
  DriverAddressSchema,
  DriverVerificationSchema,
  DriverAgeSchema,
  DriverSinNumberSchema,
  DriverVehiclesSchema,
  DriverWeeklyHoursSchema,
  DriverPaymentBankingSchema,
  DriverProfileSchema,
  DriverSettingSchema,
  AddSpecialShift,
  AddShift,
};
